import {
  CoSellAwsPartnerUserDetails,
  CoSellCompanyAndContact,
  CoSellMappingManagerSectionName,
  CosellMappingManagerSectionsForSalesforce,
  CoSellUpdateReferralRecordSalesforce,
} from './reducer'

const TEMPLATE_REQUIRED_STAGE_SUFFIX = {
  committed: 'committed',
  launched: 'launched',
  closedLost: 'closed_lost',
} as const

const parseMangledTemplatesForRequiredFieldKey = (
  key: string
): {
  fieldName: string
  stageSuffix: string
} | null => {
  if (key.endsWith('ClosedLost') && key.length > 'ClosedLost'.length) {
    return {
      fieldName: key.slice(0, -'ClosedLost'.length),
      stageSuffix: TEMPLATE_REQUIRED_STAGE_SUFFIX.closedLost,
    }
  }
  if (key.endsWith('Committed') && key.length > 'Committed'.length) {
    return {
      fieldName: key.slice(0, -'Committed'.length),
      stageSuffix: TEMPLATE_REQUIRED_STAGE_SUFFIX.committed,
    }
  }
  if (key.endsWith('Launched') && key.length > 'Launched'.length) {
    return {
      fieldName: key.slice(0, -'Launched'.length),
      stageSuffix: TEMPLATE_REQUIRED_STAGE_SUFFIX.launched,
    }
  }
  return null
}

const parseTemplatesForRequiredFieldsFromApi = (
  raw: Record<string, string> | undefined
): {
  committed: string
  launched: string
  closedLost: string
  stageFieldInternalName: string
} => {
  const out = {
    committed: '',
    launched: '',
    closedLost: '',
    stageFieldInternalName: '',
  }
  if (!raw || typeof raw !== 'object') return out
  for (const [key, val] of Object.entries(raw)) {
    const templateId = String(val ?? '').trim()
    if (!templateId) continue

    const lastDot = key.lastIndexOf('.')
    let fieldName: string
    let stageSuffix: string

    if (lastDot > 0) {
      fieldName = key.slice(0, lastDot)
      stageSuffix = key.slice(lastDot + 1)
    } else {
      const mangled = parseMangledTemplatesForRequiredFieldKey(key)
      if (!mangled) continue
      fieldName = mangled.fieldName
      stageSuffix = mangled.stageSuffix
    }

    if (!out.stageFieldInternalName && fieldName) {
      out.stageFieldInternalName = fieldName
    }
    if (stageSuffix === TEMPLATE_REQUIRED_STAGE_SUFFIX.committed) {
      out.committed = templateId
    } else if (stageSuffix === TEMPLATE_REQUIRED_STAGE_SUFFIX.launched) {
      out.launched = templateId
    } else if (stageSuffix === TEMPLATE_REQUIRED_STAGE_SUFFIX.closedLost) {
      out.closedLost = templateId
    }
  }
  return out
}

/** Maps form template ids to API `templates_for_required_fields` using `{fieldInternalName}.{stage}` keys. */
const templatesForRequiredFieldsForApi = (
  updateReferralRecord: CoSellUpdateReferralRecordSalesforce | undefined
):
  | { templates_for_required_fields: Record<string, string> }
  | Record<string, never> => {
  const internalName = String(
    updateReferralRecord?.stageFieldInternalName ?? ''
  ).trim()
  if (!internalName) return {}

  const committedTemplateId = String(
    updateReferralRecord?.committedTemplateId ?? ''
  ).trim()
  const launchedTemplateId = String(
    updateReferralRecord?.launchedTemplateId ?? ''
  ).trim()
  const closedLostTemplateId = String(
    updateReferralRecord?.closedLostTemplateId ?? ''
  ).trim()

  const out: Record<string, string> = {}
  if (committedTemplateId) {
    out[`${internalName}.${TEMPLATE_REQUIRED_STAGE_SUFFIX.committed}`] =
      committedTemplateId
  }
  if (launchedTemplateId) {
    out[`${internalName}.${TEMPLATE_REQUIRED_STAGE_SUFFIX.launched}`] =
      launchedTemplateId
  }
  if (closedLostTemplateId) {
    out[`${internalName}.${TEMPLATE_REQUIRED_STAGE_SUFFIX.closedLost}`] =
      closedLostTemplateId
  }

  if (Object.keys(out).length > 0) {
    return { templates_for_required_fields: out }
  }
  // Stage field is set but every template slot is cleared — send an empty map so PUT replaces prior template ids (omitting the key preserves them server-side).
  return { templates_for_required_fields: {} }
}

export const getTransformedDataForSalesforceApi = (
  sectionStateData: CosellMappingManagerSectionsForSalesforce
): any => {
  const awsPartnerUserDetails =
    sectionStateData[CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]
  const leadAndReferralSync =
    sectionStateData[CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]
  const autoCreateAccountAndOpportunity =
    sectionStateData[
      CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY
    ]
  const updateReferralRecordSalesforce =
    sectionStateData[
      CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE
    ]

  const transformedSyncConfiguration = {
    create_account: autoCreateAccountAndOpportunity?.autoCreateAccount ?? false,
    create_opportunity:
      autoCreateAccountAndOpportunity?.autoCreateOpportunity ?? false,
    auto_accept_incoming_referral:
      leadAndReferralSync.autoAcceptIncomingReferral || false,
    labra_referral_sync: leadAndReferralSync.labraReferralSync || false,
    partner_originated_referral_sync:
      leadAndReferralSync.partnerOriginatedReferralSync || false,
    aws_originated_referral_sync:
      leadAndReferralSync.awsOriginatedReferralSync || false,
    labra_lead_sync: leadAndReferralSync.labraLeadSync || false,
    sync_fields: {
      stage: updateReferralRecordSalesforce.stage || false,
      target_close_date:
        updateReferralRecordSalesforce.targetCloseDate || false,
      deal_owner: updateReferralRecordSalesforce.dealOwner || false,
      next_steps: updateReferralRecordSalesforce.nextSteps || false,
    },
    partner_central_details: (awsPartnerUserDetails?.userDetails ?? []).map(
      user => ({
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      })
    ),
    referral_stage_crm_stage_mapping: {
      prospect: updateReferralRecordSalesforce.stagesMapping?.prospect || [],
      qualified: updateReferralRecordSalesforce.stagesMapping?.qualified || [],
      technical_validation:
        updateReferralRecordSalesforce.stagesMapping?.technicalValidation || [],
      business_validation:
        updateReferralRecordSalesforce.stagesMapping?.businessValidation || [],
      committed: updateReferralRecordSalesforce.stagesMapping?.committed || [],
      launched: updateReferralRecordSalesforce.stagesMapping?.launched || [],
      closed_lost:
        updateReferralRecordSalesforce.stagesMapping?.closedLost || [],
    },
    ...templatesForRequiredFieldsForApi(updateReferralRecordSalesforce),
  }

  return {
    syncConfiguration: transformedSyncConfiguration,
    autoCreateMappings:
      autoCreateAccountAndOpportunity?.autoCreateMappings ?? {},
  }
}

export const getTransformedDataFromSalesforceApi = (
  sectionData: any,
  rawSectionData?: any
): any => {
  const config = sectionData?.syncConfiguration || {}
  const contacts = config.contacts || {}
  const syncFields = config.syncFields || {}
  const partnerCentralDetails = config.partnerCentralDetails || []
  const referralStageCrmStageMapping =
    config.referralStageCrmStageMapping ||
    config.referral_stage_crm_stage_mapping ||
    {}

  const templatesForRequiredFieldsRaw = config.templatesForRequiredFields ?? {}

  const parsedTemplatesForRequiredFields =
    parseTemplatesForRequiredFieldsFromApi(
      templatesForRequiredFieldsRaw as Record<string, string>
    )

  const autoCreateMappings =
    rawSectionData?.auto_create_mappings != null &&
    typeof rawSectionData.auto_create_mappings === 'object'
      ? rawSectionData.auto_create_mappings
      : (config.autoCreateMappings ??
        config.auto_create_mappings ??
        sectionData?.autoCreateMappings ??
        sectionData?.auto_create_mappings ??
        {})

  return {
    [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
      createCompany: config.createCompany ?? false,
      createContact: config.createContact ?? false,
      createSenderCompany: config.createSenderCompany ?? false,
      awsSalesRep: contacts.awsSalesRep || false,
      awsAccountManager: contacts.awsAccountManager || false,
      awsIsvSuccessManager: contacts.awsIsvSuccessManager || false,
      awsPartnerSuccessManager: contacts.awsPartnerSuccessManager || false,
      partnerDevelopmentManager: contacts.partnerDevelopmentManager || false,
      wwpsPartnerDevelopmentManager:
        contacts.wwpsPartnerDevelopmentManager || false,
      customerEndUserContactDetails:
        contacts.customerEndUserContactDetails || false,
      senderContact: contacts.senderContact || false,
    } as CoSellCompanyAndContact,

    [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
      userDetails: partnerCentralDetails.map((user: any, index: number) => ({
        id: `${index}-${user.username || Math.random().toString(36).substring(2, 10)}`,
        email: user.email || '',
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
      })),
    } as CoSellAwsPartnerUserDetails,

    [CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]: {
      labraReferralSync: config.labraReferralSync ?? false,
      partnerOriginatedReferralSync:
        config.partnerOriginatedReferralSync ?? false,
      awsOriginatedReferralSync: config.awsOriginatedReferralSync ?? false,
      labraLeadSync: config.labraLeadSync ?? false,
      autoAcceptIncomingReferral: config.autoAcceptIncomingReferral ?? false,
    },

    [CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY]: {
      autoCreateAccount: config.createAccount ?? false,
      autoCreateOpportunity: config.createOpportunity ?? false,
      syncReferralDataToOpportunity:
        config.syncReferralDataToOpportunity ?? false,
      autoCreateMappings,
    },

    [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE]: {
      stage: syncFields.stage || false,
      targetCloseDate: syncFields.targetCloseDate || false,
      nextSteps: syncFields.nextSteps || false,
      stageFieldInternalName:
        parsedTemplatesForRequiredFields.stageFieldInternalName || '',
      committedTemplateId: parsedTemplatesForRequiredFields.committed || '',
      launchedTemplateId: parsedTemplatesForRequiredFields.launched || '',
      closedLostTemplateId: parsedTemplatesForRequiredFields.closedLost || '',
      dealOwner: syncFields.dealOwner || false,
      stagesMapping: {
        prospect: referralStageCrmStageMapping.prospect || [],
        qualified: referralStageCrmStageMapping.qualified || [],
        technicalValidation:
          referralStageCrmStageMapping.technical_validation ||
          referralStageCrmStageMapping.technicalValidation ||
          [],
        businessValidation:
          referralStageCrmStageMapping.business_validation ||
          referralStageCrmStageMapping.businessValidation ||
          [],
        committed: referralStageCrmStageMapping.committed || [],
        launched: referralStageCrmStageMapping.launched || [],
        closedLost:
          referralStageCrmStageMapping.closed_lost ||
          referralStageCrmStageMapping.closedLost ||
          [],
      },
    } as CoSellUpdateReferralRecordSalesforce,
  }
}
